/**
 * Inline +/- accordion topic picker (Sam, Oct 21 2026). Category › subtopic ›
 * sub-subtopic, a checkbox at every level, everything starts collapsed.
 * Used by search (match any ticked) and the listener profile Topics step.
 * value / onChange: array of topic paths.
 */
import React, { useState } from 'react';
import { CATEGORIES, nodeFor, isFeaturedPath } from '../../util/topicPaths';
import css from './TopicAccordion.module.css';

// Sam, Oct 21 2026: Wanting company is pickable again.
const HIDDEN_IDS = new Set([]);
const kids = path => {
  const n = nodeFor(path);
  return ((n && n.children) || [])
    .filter(c => !HIDDEN_IDS.has(c.id))
    .map(c => `${path}/${c.id}`)
    .filter(isFeaturedPath);
};
const top = id => (CATEGORIES.find(c => c.id === id) ? id : null);

// Top categories built from the existing taxonomy (Sam, Oct 21 2026: Health and Gender, Identity & Sexuality shown too).
export const TOPIC_GROUPS = [
  { id: 'everyday', label: 'Everyday talk & company', nodes: () => ['just-talk', 'new-in-town', 'life-after-55'].filter(top) },
  { id: 'love', label: 'Love, family & friends', nodes: () => kids('life') },
  { id: 'women', label: 'Women supporting women', nodes: () => ['women-supporting-women', 'womens-issues'].filter(top) },
  { id: 'work', label: 'Work, money & growth', nodes: () => [...['mentorship-life-guidance'].filter(top), ...kids('work')] },
  { id: 'sports', label: 'Sports & games', nodes: () => kids('sports') },
  { id: 'shows', label: 'Shows, music & books', nodes: () => kids('shows') },
  { id: 'world', label: 'News, tech & the world', nodes: () => kids('world') },
  { id: 'hobbies', label: 'Hobbies, pets & home', nodes: () => kids('hobbies') },
  { id: 'health', label: 'Health & aging', nodes: () => kids('health') },
  { id: 'identity', label: 'Gender, Identity & Sexuality', nodes: () => kids('gender-identity-sexuality') },
];

const OPEN_ROWS = {};
const labelOf = p => nodeFor(p)?.label || p;
const isUnder = (p, a) => p === a || p.startsWith(`${a}/`);

const Row = ({ k, label, checked, indeterminate, onToggle, hasKids, level, testPath, open, toggleOpen }) => (
  <div className={css.row} data-level={level} data-topic-row={testPath}>
    {hasKids ? (
      <button
        type="button"
        className={css.plus}
        aria-expanded={!!open[k]}
        aria-label={`${open[k] ? 'Collapse' : 'Expand'} ${label}`}
        onClick={() => toggleOpen(k)}
        data-topic-toggle={testPath}
      >
        {open[k] ? '−' : '+'}
      </button>
    ) : (
      <span className={css.plusSpacer} />
    )}
    <label className={css.label}>
      <input
        type="checkbox"
        checked={checked}
        ref={el => el && (el.indeterminate = !!indeterminate)}
        onChange={onToggle}
        data-topic-check={testPath}
      />
      <span>{label}</span>
    </label>
  </div>
);

const TopicAccordion = ({ value = [], onChange, limit = 40, idPrefix = 'ta' }) => {
  const sel = Array.isArray(value) ? value : [];
  // Kept outside React state too: some forms remount the picker on every change,
  // which would snap it shut. Fresh page loads still start fully collapsed.
  const [open, setOpenState] = useState(() => ({ ...(OPEN_ROWS[idPrefix] || {}) }));
  const toggleOpen = k => {
    const cur = OPEN_ROWS[idPrefix] || open;
    const next = { ...cur, [k]: !cur[k] };
    OPEN_ROWS[idPrefix] = next;
    setOpenState(next);
  };
  const covered = p => sel.some(s => isUnder(p, s));
  const partial = p => !covered(p) && sel.some(s => isUnder(s, p));
  const [capHit, setCapHit] = useState(false);
  const atCap = sel.length >= limit;
  const emit = next => {
    const uniq = [...new Set(next)];
    if (uniq.length > limit && uniq.length > sel.length) {
      // Sam, Oct 29 2026: don't silently drop extra ticks — tell them.
      setCapHit(true);
      return;
    }
    setCapHit(false);
    onChange && onChange(uniq.slice(0, limit));
  };
  const check = p => emit([...sel.filter(s => !isUnder(s, p)), p]);
  const uncheck = p => {
    const anc = sel.find(s => isUnder(p, s) && s !== p);
    let next = sel.filter(s => !isUnder(s, p));
    if (anc) {
      // Ancestor was ticked: keep its other branches ticked.
      next = next.filter(s => s !== anc);
      let cur = anc;
      while (cur !== p) {
        const parts = p.split('/');
        const childOnPath = parts.slice(0, cur.split('/').length + 1).join('/');
        next.push(...kids(cur).filter(k => k !== childOnPath));
        cur = childOnPath;
      }
    }
    emit(next);
  };
  const groupNodes = g => g.nodes();
  const groupState = g => {
    const ns = groupNodes(g);
    const all = ns.length > 0 && ns.every(covered);
    const some = !all && ns.some(n => covered(n) || partial(n));
    return { all, some };
  };


  const renderNode = (p, level) => {
    const ks = level < 3 ? kids(p) : [];
    const k = `${idPrefix}:${p}`;
    return (
      <li key={p}>
        <Row
          k={k}
          label={labelOf(p)}
          checked={covered(p)}
          indeterminate={partial(p)}
          onToggle={() => (covered(p) ? uncheck(p) : check(p))}
          hasKids={ks.length > 0}
          level={level}
          testPath={p}
          open={open}
          toggleOpen={toggleOpen}
        />
        {ks.length && open[k] ? <ul className={css.list}>{ks.map(c => renderNode(c, level + 1))}</ul> : null}
      </li>
    );
  };

  const capMsg = capHit
    ? `You've reached the ${limit}-topic limit. Uncheck one to add another.`
    : atCap
    ? `You've picked ${limit} topics, the most you can have. Uncheck one to add another.`
    : null;

  return (
    <>
    {capMsg ? (
      <p className={css.capNote} role="status" aria-live="polite" data-topic-cap-note="true">
        {capMsg}
      </p>
    ) : null}
    <ul className={`${css.root} ${atCap ? css.atCap : ''}`} data-topic-accordion="true" data-topic-at-cap={atCap ? 'true' : 'false'}>
      {TOPIC_GROUPS.map(g => {
        const { all, some } = groupState(g);
        const k = `${idPrefix}:g:${g.id}`;
        return (
          <li key={g.id} className={css.group}>
            <Row
              k={k}
              label={g.label}
              checked={all}
              indeterminate={some}
              onToggle={() => {
                const ns = groupNodes(g);
                if (all) emit(sel.filter(s => !ns.some(n => isUnder(s, n))));
                else emit([...sel.filter(s => !ns.some(n => isUnder(s, n))), ...ns]);
              }}
              hasKids
              level={1}
              testPath={`group:${g.id}`}
              open={open}
              toggleOpen={toggleOpen}
            />
            {open[k] ? <ul className={css.list}>{groupNodes(g).map(n => renderNode(n, 2))}</ul> : null}
          </li>
        );
      })}
    </ul>
    {capHit ? (
      <p className={css.capNote} role="status" data-topic-cap-note-bottom="true">
        {capMsg}
      </p>
    ) : null}
    </>
  );
};

export default TopicAccordion;
